import { AlertTriangle, BookOpen, CheckCircle2, ChevronRight, Copy, Save } from 'lucide-react';
import { href } from '../../lib/router';
import type { ConfigItem } from './api';
import type { useItemDraft } from './draft';
import type { Issue } from './editors';
import { sameJson } from './journey';
import { SCOPE_LABEL, when } from './StudioPage';

/** The heading of a question or rule editor: where it sits, its scope and revision, and Save or Customise. */
export function EditorHead<T>({
  d,
  section,
  sectionHref,
  title,
  blocked,
}: {
  d: Pick<
    ReturnType<typeof useItemDraft<T>>,
    'item' | 'dirty' | 'owns' | 'canCustomise' | 'saving' | 'save' | 'customise' | 'draft' | 'setDraft'
  >;
  section: string;
  sectionHref: string;
  title: string;
  blocked: boolean;
}) {
  const it = d.item.data as ConfigItem;
  return (
    <>
      <div className="crumbs">
        <a href={href.studio()}>Studio</a>
        <ChevronRight size={14} />
        <a href={sectionHref}>{section}</a>
        <ChevronRight size={14} />
        {it.key}
      </div>
      <div className="page-head row-between">
        <div>
          <h1 className="clamp-2">{title || it.key}</h1>
          <p>
            <span className="mono">{it.key}</span> · <span className="pill neutral">{SCOPE_LABEL[it.scope_level] ?? it.scope_level}</span> · revision {it.revision}
            {d.dirty && <span className="pill warn-pill ml">Unsaved changes</span>}
          </p>
        </div>
        {d.owns ? (
          <button type="button" className="btn btn-primary" onClick={d.save} disabled={!d.dirty || blocked || d.saving} title={blocked ? 'Fix the problems first' : undefined}>
            <Save size={16} /> {d.saving ? 'Saving…' : 'Save draft'}
          </button>
        ) : (
          d.canCustomise && (
            <button type="button" className="btn btn-primary" onClick={d.customise} disabled={d.saving}>
              <Copy size={16} /> Customise for your organisation
            </button>
          )
        )}
      </div>
      <LibraryNotice item={it} draft={d.draft} editable={d.owns} onLoad={(b) => d.setDraft(() => b as T)} />
      {!d.owns && (
        <div className="notice neutral mb">
          {d.canCustomise
            ? 'This comes from the platform. Customise it to change it for your organisation; the platform version stays as it is.'
            : 'You can look, but only configuration editors make changes.'}
        </div>
      )}
    </>
  ); // prettier-ignore
}

/**
 * A platform item changed in the studio while the control library gives another version (task 95). Deploys
 * keep the studio's; the editor can load the library's into the draft, to be saved and released like any
 * change.
 */
export function LibraryNotice({
  item,
  draft,
  editable,
  onLoad,
}: {
  item: ConfigItem;
  draft: unknown;
  editable: boolean;
  onLoad: (body: Record<string, unknown>) => void;
}) {
  const lib = item.library;
  if (!lib || sameJson(lib.body, item.body)) return null;
  const loaded = sameJson(lib.body, draft);
  return (
    <div className="notice neutral mb library-notice" role="status" aria-label="Control library version">
      <BookOpen size={16} />
      <span>
        {loaded
          ? 'The control library’s version is loaded. Save the draft to keep it; a release publishes it.'
          : `Changed in the studio: deploys keep this version. The control library’s version differs (library updated ${when(lib.updated_at)}).`}
      </span>
      {editable && !loaded && (
        <button type="button" className="linklike" onClick={() => onLoad(structuredClone(lib.body))}>
          Load the library’s version
        </button>
      )}
    </div>
  );
}

/** What stops the draft being saved, in plain words, or a line saying it is fine. */
export function IssueList({ issues, label }: { issues: Issue[]; label: (path: string) => string }) {
  return (
    <section className={`card pad ${issues.length ? 'problems' : ''}`} aria-live="polite">
      {issues.length ? (
        <>
          <div className="eyebrow row gap">
            <AlertTriangle size={14} className="warn-t" /> To fix before saving ({issues.length})
          </div>
          <ul className="problem-list">
            {issues.map((p, i) => (
              <li key={i}>
                <b>{label(p.path)}:</b> {p.message}
              </li>
            ))}
          </ul>
        </>
      ) : (
        <div className="ok-line">
          <CheckCircle2 size={16} className="ok-t" /> Ready to save.
        </div>
      )}
    </section>
  );
}

/** A comma-separated list as an array, and back. */
export const fromList = (s: string) => s.split(',').map((x) => x.trim()).filter(Boolean); // prettier-ignore
export const toList = (a: readonly string[] | undefined) => (a ?? []).join(', ');
